import * as THREE from 'three';

// Loads the rose window tools/build-rosace.mjs cut into pieces - the glass
// photo, the lossless id map, the lead/tracery masks and the per-piece data -
// once, at application startup, and hands them to visualizers/rosace.js
// through a getter. Same preload/get shape as paper-texture.js: nothing is
// fetched inside init(), and the preload is a WIRING POINT every entry point
// must call (main, main-radio, main-embedded, play-boot, landing-boot,
// demo-boot, and the capture harness). A forgotten one is silent: the getter
// returns null and the rose stays dark.
//
// THE ID MAP IS DATA, NOT A PICTURE. It is read with NearestFilter, no
// mipmaps and no colour-space conversion: a linear filter or an sRGB decode
// would interpolate two neighbouring ids into a value no piece has, and a
// mipmap would do the same over whole regions. The glass is an ordinary sRGB
// photo and gets the ordinary treatment.
const MANIFEST_URL = 'textures/rosace.json';

let _assets = null;
let _promise = null;

function loadTexture(url, configure) {
  return new Promise(function (resolve, reject) {
    new THREE.TextureLoader().load(url, function (t) { configure(t); t.needsUpdate = true; resolve(t); }, undefined, function () {
      reject(new Error(url + ' failed to load'));
    });
  });
}

// Which resolution: the 2048 only where the drawing buffer can show it.
function pickSize(sizes) {
  const px = (typeof window !== 'undefined')
    ? (window.innerWidth || 1280) * Math.min(window.devicePixelRatio || 1, 2) : 1280;
  const sorted = sizes.map(Number).sort((a, b) => a - b);
  // The smallest size that covers the drawing buffer; the Pi's 1920 wide
  // kiosk lands on 1400 (its tiers never show the 2048, see rosace.js), a 4K
  // or Retina screen on 2048.
  for (const s of sorted) if (s >= px * 0.72) return String(s);
  return String(sorted[sorted.length - 1]);
}

export function preloadRosace() {
  if (_promise) return _promise;
  _promise = fetch(MANIFEST_URL)
    .then(function (r) {
      if (!r.ok) throw new Error('rosace.json HTTP ' + r.status);
      return r.json();
    })
    .then(function (manifest) {
      const size = pickSize(Object.keys(manifest.glass));
      const idSize = pickSize(Object.keys(manifest.ids));
      return Promise.all([
        loadTexture('textures/' + manifest.glass[size], function (t) {
          t.colorSpace = THREE.SRGBColorSpace;
          t.generateMipmaps = true;
          t.minFilter = THREE.LinearMipmapLinearFilter;
          t.magFilter = THREE.LinearFilter;
          t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
          t.anisotropy = 4;
        }),
        loadTexture('textures/' + manifest.ids[idSize], function (t) {
          t.colorSpace = THREE.NoColorSpace;
          t.generateMipmaps = false;
          t.minFilter = THREE.NearestFilter;
          t.magFilter = THREE.NearestFilter;
          t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
        }),
        loadTexture('textures/' + manifest.masks, function (t) {
          t.colorSpace = THREE.NoColorSpace;
          t.generateMipmaps = true;
          t.minFilter = THREE.LinearMipmapLinearFilter;
          t.magFilter = THREE.LinearFilter;
          t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
        }),
        fetch('textures/' + manifest.data).then(function (r) {
          if (!r.ok) throw new Error(manifest.data + ' HTTP ' + r.status);
          return r.json();
        })
      ]).then(function (parts) {
        _assets = { manifest: manifest, glass: parts[0], ids: parts[1], masks: parts[2], data: parts[3], size: Number(size), idSize: Number(idSize) };
        return _assets;
      });
    })
    .catch(function (err) {
      console.error('[party-mode] rosace assets unavailable, the rose will stay dark', err);
      _assets = null;
      return null;
    });
  return _promise;
}

export function getRosace() {
  return _assets;
}

// Node tests only: hand in assets without a fetch.
export function _setRosaceForTests(a) { _assets = a; _promise = Promise.resolve(a); }

// The larger glass for tier 2. The preload takes what the screen needs; a
// tier-2 visualizer may ask for the 2048 on top and swaps it in when it lands.
// Cached per size, never loaded twice.
const _sized = {};
export function ensureRosaceGlass(size) {
  const key = String(size);
  if (_sized[key]) return _sized[key];
  _sized[key] = preloadRosace().then(function (a) {
    if (!a) return null;
    if (a.size === Number(size)) return { glass: a.glass, ids: a.ids, size: a.size };
    const m = a.manifest;
    if (!m.glass[key] || !m.ids[key]) return null;
    return Promise.all([
      loadTexture('textures/' + m.glass[key], function (t) {
        t.colorSpace = THREE.SRGBColorSpace;
        t.generateMipmaps = true;
        t.minFilter = THREE.LinearMipmapLinearFilter;
        t.magFilter = THREE.LinearFilter;
        t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
        t.anisotropy = 4;
      }),
      loadTexture('textures/' + m.ids[key], function (t) {
        t.colorSpace = THREE.NoColorSpace;
        t.generateMipmaps = false;
        t.minFilter = THREE.NearestFilter;
        t.magFilter = THREE.NearestFilter;
        t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
      })
    ]).then(function (parts) { return { glass: parts[0], ids: parts[1], size: Number(size) }; });
  }).catch(function (err) {
    console.warn('[party-mode] rosace ' + key + ' unavailable', err);
    return null;
  });
  return _sized[key];
}
